"use client";

import { useEffect } from "react";

import { buildTransferProgressStorageKey } from "@/components/transfer-progress/transfer-progress-storage";
import {
  FUN_IMPORTED_PAYMENTS_KEY,
  SUI_OWNER_KEY_MIGRATED_KEY,
  XCHAIN_IMPORTED_DEPOSITS_KEY,
  XCHAIN_IMPORTED_WITHDRAWALS_KEY,
} from "@/constants/local-storage-keys";
import { type BoundedKeySet, boundedKeySet } from "@/hooks/storage/bounded-key-set";
import { getParsedLocalStorageItem, setLocalStorageItem } from "@/hooks/storage/use-local-storage";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";

/**
 * Re-keys the owner-scoped local ledgers for Sui users.
 *
 * Before the session-bound Sui address landed, `ownerWalletAddress` resolved to the derived Aptos
 * address for a Sui session, so every ledger namespaced by it was written under that address.
 * Reading them under the Sui address without this would look to the user like their transfer
 * history had been deleted.
 *
 * The old keys are deliberately left in place: the sentinel, not deletion, is what makes this
 * idempotent, and keeping them makes a rollback to the previous build non-destructive. A future
 * cleanup PR must not "finish the job".
 */

const suiOwnerMigrations = boundedKeySet(SUI_OWNER_KEY_MIGRATED_KEY, 50);
const importedDeposits = boundedKeySet(XCHAIN_IMPORTED_DEPOSITS_KEY, 200);
const importedWithdrawals = boundedKeySet(XCHAIN_IMPORTED_WITHDRAWALS_KEY, 200);
const importedFunPayments = boundedKeySet(FUN_IMPORTED_PAYMENTS_KEY, 200);

type LedgerFactory = (...scope: string[]) => BoundedKeySet;

interface IdentifiedEntry {
  id?: unknown;
}

/** `StorageValue` includes `object`, so `Array.isArray` alone narrows to `any[]`. */
function readEntries(key: string): IdentifiedEntry[] | null {
  const parsed = getParsedLocalStorageItem(key);
  return Array.isArray(parsed) ? (parsed as IdentifiedEntry[]) : null;
}

function migrateTransferProgress(oldOwner: string, newOwner: string) {
  const oldKey = buildTransferProgressStorageKey(oldOwner);
  const newKey = buildTransferProgressStorageKey(newOwner);

  // Absent or corrupt: nothing to move, and clobbering the new key with `[]` would be worse.
  const oldEntries = readEntries(oldKey);
  if (!oldEntries) return;

  const newEntries = readEntries(newKey) ?? [];

  // Union rather than overwrite — a user who rolled back and forward again has records under
  // both keys. New-key entries win on an `id` collision and keep their relative order.
  // An id-less entry matches nothing: without the guard, one of them under the new key would
  // swallow every id-less entry under the old one.
  const newIds = new Set(newEntries.map((entry) => entry.id).filter((id) => id !== undefined));
  const merged = [
    ...oldEntries.filter((entry) => entry.id === undefined || !newIds.has(entry.id)),
    ...newEntries,
  ];

  // `setLocalStorageItem` (not raw `localStorage.setItem`) so mounted `useLocalStorage`
  // subscribers re-render with the migrated data.
  setLocalStorageItem(newKey, merged);
}

function migrateKeySet(ledger: LedgerFactory, oldOwner: string, newOwner: string) {
  const oldKeys = ledger(oldOwner).read();
  if (oldKeys.size === 0) return;
  ledger(newOwner).add(...oldKeys);
}

export function useSuiOwnerStorageMigration(): void {
  const { chainName, suiAddress, accountAddress } = useDecibelWallet();

  useEffect(() => {
    if (chainName !== "Sui") return;
    if (!suiAddress || !accountAddress) return;
    if (addressesEqual(suiAddress, accountAddress)) return;

    const sentinel = suiOwnerMigrations();
    const migrated = suiAddress.toLowerCase();
    if (sentinel.read().has(migrated)) return;

    try {
      migrateTransferProgress(accountAddress, suiAddress);
      migrateKeySet(importedDeposits, accountAddress, suiAddress);
      migrateKeySet(importedWithdrawals, accountAddress, suiAddress);
      migrateKeySet(importedFunPayments, accountAddress, suiAddress);
    } catch (error) {
      Logger.warn("Failed to migrate Sui owner-keyed storage", { error });
    } finally {
      // Marked done even on failure. The old keys still hold the data, so stopping costs
      // nothing, while retrying on every mount would hammer localStorage forever.
      sentinel.add(migrated);
    }
  }, [chainName, suiAddress, accountAddress]);
}
